import { useState } from 'react'
import { useGetSelfHealingTargetsQuery, useSaveSelfHealingTargetsMutation, useGetTestingSetupPlanQuery } from '../services/sessionMonitoringApi'
import { SelfHealingTargetsSchema, type SelfHealingTargets as Targets } from '../schemas/selfHealing'
import { selfHealingSessionLimit } from '../lib/selfHealingLimits'

const amounts = [0, 5, 20, 100]

function TargetsEditor({ projectId, initial, canEdit }: { projectId: string; initial: Targets; canEdit: boolean }) {
  const plan = useGetTestingSetupPlanQuery(projectId)
  const maximum = selfHealingSessionLimit(plan.currentData?.plan_key)
  const [count, setCount] = useState(initial.analyses_per_day)
  const [other, setOther] = useState(!amounts.includes(initial.analyses_per_day))
  const [save, request] = useSaveSelfHealingTargetsMutation()
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const busy = !canEdit || request.isLoading || !plan.currentData || plan.isError
  return <form className="self-healing__form" onSubmit={async event => {
    event.preventDefault(); setError(''); setMessage('')
    if (busy) return
    const parsed = SelfHealingTargetsSchema.safeParse({ reviews_per_day: count, analyses_per_day: count })
    if (!parsed.success || count > maximum) { setError(`Enter a whole number from 0 to ${maximum.toLocaleString()}.`); return }
    try {
      await save({ projectId, targets: parsed.data }).unwrap()
      setMessage('Daily session limit saved.')
    } catch { setError('Could not save the daily session limit. Please try again.') }
  }}>
    {plan.isLoading && <p role="status">Checking your plan…</p>}
    {plan.isError && <p role="alert">Could not check your plan. <button type="button" onClick={() => void plan.refetch()}>Retry</button></p>}
    <fieldset disabled={busy}>
      <legend>Sessions to analyze each day</legend>
      <div className="continuous-testing-setup__amounts self-healing__amounts">
        {amounts.map(amount => <button key={amount} type="button" disabled={amount > maximum} title={amount > maximum ? 'Choose a higher plan to increase this limit.' : undefined} aria-pressed={!other && count === amount} onClick={() => { setOther(false); setCount(amount) }}>{amount === 0 ? 'None' : amount}</button>)}
        <button type="button" aria-pressed={other} onClick={() => setOther(true)}>Other</button>
        {other && <input aria-label="Sessions to analyze each day" type="number" required min="0" max={maximum} step="1" value={Number.isNaN(count) ? '' : count} onChange={event => setCount(event.target.valueAsNumber)} />}
      </div>
      <p>Your plan allows up to {maximum.toLocaleString()} sessions per day. Each reviewer run costs 0.2 credits.</p>
      {canEdit && <button type="submit" className="testing-setup__primary" disabled={!Number.isInteger(count) || count < 0 || count > maximum}>{request.isLoading ? 'Saving…' : 'Save daily limit'}</button>}
    </fieldset>
    {message && <p role="status">{message}</p>}
    {error && <p role="alert">{error}</p>}
  </form>
}

export default function SelfHealingTargets({ projectId }: { projectId: string }) {
  const query = useGetSelfHealingTargetsQuery(projectId)
  return <section>
    {query.isLoading && <p role="status">Loading daily limit…</p>}
    {query.isError && <p role="alert">Could not load the daily limit. <button type="button" onClick={() => void query.refetch()}>Retry</button></p>}
    {query.currentData && <TargetsEditor key={projectId} projectId={projectId} initial={query.currentData.targets} canEdit={query.currentData.can_edit} />}
  </section>
}
